import { css } from "@emotion/react";
import { ApiContext } from "@hermes/api";
import { useLocale } from "@hermes/intl";
import {
  HermesAppContainer,
  makeDefaultAxiosInstance,
  makeDefaultQueryClient,
} from "@hermes/shared";
import {
  cssFns,
  Spinner,
  usePrincipalColors,
  useScreenDetector,
} from "@hermes/ui";
import { captureException } from "@sentry/react";
import { APIProvider } from "@vis.gl/react-google-maps";
import { type ReactNode, Suspense, useEffect } from "react";
import {
  Navigate,
  Outlet,
  ScrollRestoration,
  useLocation,
  useMatches,
  useNavigation,
  useParams,
} from "react-router-dom";

import { CityAreaProvider } from "#internal/features/city/city-area";
import { CityProvider } from "#internal/features/city/city-provider";
import { Footer } from "#internal/features/footer";
import { Header } from "#internal/features/header";
import { SubHeader } from "#internal/features/header/sub-header";
import { messageCache } from "#internal/intl";
import { BtnScrollTop } from "#internal/shared/ui/btn-scroll-top";
import { usePrerenderReady, useStoredLocale } from "#internal/shared/utils";

import { GTagWrapper } from "./analytics";
import { AppConfigContext, useAppConfig } from "./config";
import { Flags } from "./flags";
import { useRoutePath } from "./router";

const ContentWrapper = ({ children }: { children: ReactNode }) => {
  usePrerenderReady();
  const { isDesktop } = useScreenDetector();
  const principalColors = usePrincipalColors();
  const locale = useLocale();
  const matches = useMatches();
  const { state: navigationState } = useNavigation();
  const outletRoute = matches[1];

  const isHomeRoute = outletRoute?.id === "home";
  const isDoctorRoute = outletRoute?.id === "doctor-profile";

  const headerHeight = isDesktop ? "80px" : "50px";

  const loadingFallback = (
    <div
      css={[
        cssFns.center,
        {
          flexGrow: 1,
          minHeight: `calc(100vh - ${headerHeight})`,
          paddingBlockEnd: headerHeight,
        },
      ]}
    >
      <Spinner />
    </div>
  );

  return (
    <div
      css={{
        color: principalColors.gs2,
        backgroundColor: principalColors.gs10,
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Header
        variant={isHomeRoute && isDesktop ? "transparent" : undefined}
        height={headerHeight}
      />
      {isDesktop && !isHomeRoute && <SubHeader />}
      <main css={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <Suspense fallback={loadingFallback}>
          <APIProvider
            apiKey={import.meta.env.DOQ_GOOGLE_MAP_KEY}
            language={locale.toString()}
          >
            {children}
            {navigationState === "loading" && loadingFallback}
          </APIProvider>
        </Suspense>
      </main>
      <Footer />
      <BtnScrollTop isDoctorRoute={isDoctorRoute} />
    </div>
  );
};

const queryClient = makeDefaultQueryClient();
export const axiosInstance = makeDefaultAxiosInstance({
  baseURL: import.meta.env.DOQ_DEBUG_API_URL || import.meta.env.DOQ_API_URL,
});
const SCROLL_POSITIONS_KEY = "scroll-positions";

export const AppContainer = () => {
  useEffect(() => {
    // Reset scroll positions on page refresh
    sessionStorage.removeItem(SCROLL_POSITIONS_KEY);
  }, []);

  return (
    <HermesAppContainer
      axiosInstance={axiosInstance}
      queryClient={queryClient}
      countryId={import.meta.env.DOQ_COUNTRY_ID}
      cookieDomain={import.meta.env.DOQ_COOKIE_DOMAIN}
      autoSendToken={false}
      messageCache={messageCache}
      globalStyles={css([
        {
          "#root .react-loading-skeleton": {
            lineHeight: "24px",
          },
        },
      ])}
      storedLocaleKey="locale"
      polyFillExclude={["plural-rules"]}
      onRequestError={(error) => {
        if (error.response && error.response.status >= 500) {
          captureException(error, { level: "fatal" });
        }
      }}
    >
      <ApiContext
        name="front"
        instance={makeDefaultAxiosInstance({
          baseURL: import.meta.env.DOQ_FRONT_API_URL,
        })}
      >
        <ScrollRestoration storageKey={SCROLL_POSITIONS_KEY} />
        <AppConfigContext>
          <Flags>
            <CityProvider>
              <CityAreaProvider>
                <ContentWrapper>
                  <GTagWrapper>
                    <LangHandler>
                      <Outlet />
                    </LangHandler>
                  </GTagWrapper>
                </ContentWrapper>
              </CityAreaProvider>
            </CityProvider>
          </Flags>
        </AppConfigContext>
      </ApiContext>
    </HermesAppContainer>
  );
};

const LangHandler = ({ children }: { children: ReactNode }) => {
  const { routePath } = useRoutePath();
  const { country } = useAppConfig();
  const params = useParams();
  const location = useLocation();
  const routeLocale = params["lang"];
  const [storedLocale, setStoredLocale] = useStoredLocale();

  const exists =
    routeLocale && (country.locales as string[]).includes(routeLocale);
  const condition = exists && routeLocale !== storedLocale;

  // Sync store locale with route locale once
  useEffect(() => {
    if (condition) {
      setStoredLocale(routeLocale);
    }
  }, []);

  // handle 404 for unknown route locale
  if (routeLocale && !exists && !location.pathname.includes("404")) {
    return <Navigate to={routePath({ path: "/404" })} replace />;
  }

  return children;
};
